import { Box, Flex, Grid, Heading, Text } from "@radix-ui/themes";
import { HeroStyle2 } from "~/components/HeroStyle2";
import { HomeConsultForm } from "~/components/home/HomeConsultForm";
import {
	Locale,
	useLocalization,
} from "~/components/localization/LocalizationProvider";
import { OptimizedImage } from "~/components/OptimizedImage";
import { Link } from "~/components/RouterLink";
import styles from "../live-seo-page.module.css";

type InvisalignMiamiLocalization = {
	title: string;
	introHeading: string;
	introFirst: string;
	introSecondPrefix: string;
	introSecondInvisalign: string;
	introSecondMiddle: string;
	introSecondBraces: string;
	introSecondSuffix: string;
	introSecondFinalLink: string;
	introSecondEnd: string;
	whatHeading: string;
	whatParagraphs: string[];
	contactLinkText: string;
	contactSuffix: string;
	homeLinkText: string;
	initialConsultSuffix: string;
	facebookLinkText: string;
	traySuffix: string;
	discoverHeading: string;
	discoverFirst: string;
	discoverSecondPrefix: string;
	discoverSecondLink: string;
	discoverSecondSuffix: string;
};

const invisalignMiamiLocalization = {
	[Locale.EN]: {
		title: "Invisalign Miami",
		introHeading: "Your Choice for Invisalign in Miami",
		introFirst: " are the premier Invisalign provider in the Miami area.",
		introSecondPrefix: "Have you been thinking of orthodontic treatment? ",
		introSecondInvisalign: "Invisalign",
		introSecondMiddle:
			" is a great option for anyone looking for an alternative to ",
		introSecondBraces: "traditional braces",
		introSecondSuffix: ". We are here to help you determine if ",
		introSecondFinalLink: "Invisalign",
		introSecondEnd: " is right for you.",
		whatHeading: "What is Invisalign?",
		whatParagraphs: [
			"The Invisalign system is made of clear, lightweight plastic aligners that fit over your teeth and are designed to straighten your smile. They’re removable, making it easier for our patients in the Miami area to eat, drink, brush and floss.",
			"Their removability means you do not need to relearn how to brush and floss, nor do you need to change your diet. And since they are comfortable, you will often forget you are wearing the aligners. Our Invisalign patients at ",
			"Invisalign feels way more comfortable than traditional metal bracket braces and can give you the results you've always wanted.",
			"You’ll probably wear Invisalign aligners for 9–15 months and will typically use 18–30 sets during your treatment. You’ll even start to notice a change in just a few months!",
			"At your initial consultation at ",
			"You wear the trays for about 22 hours a day but can remove them to brush your teeth and eat. Based on your individual needs and the treatment plan put in place by ",
		],
		contactLinkText: "Miami Orthodontics Specialists",
		contactSuffix: " love the system, and you will, too.",
		homeLinkText: "Miami Orthodontic Specialists",
		initialConsultSuffix:
			", we'll take digital scans and X-rays to create a 3D image of your mouth. We make your first set of custom-fitted aligners from these images, and then you begin the process of straightening your teeth.",
		facebookLinkText: "Miami Orthodontic Specialists",
		traySuffix:
			", you’ll wear several sets of trays throughout your orthodontic treatment.",
		discoverHeading: "Discover Invisalign in Miami",
		discoverFirst:
			"Visit our Miami office to explore orthodontic treatment options and see if Invisalign is the right fit for you! Dr. Garcia-Paul is here to help you through your orthodontic journey.",
		discoverSecondPrefix: "Schedule a ",
		discoverSecondLink: "free consultation",
		discoverSecondSuffix: " today!",
	},
	[Locale.ES]: {
		title: "Invisalign Miami",
		introHeading: "Tu opción para Invisalign en Miami",
		introFirst:
			" son el proveedor principal de Invisalign en el área de Miami.",
		introSecondPrefix: "¿Has estado pensando en un tratamiento de ortodoncia? ",
		introSecondInvisalign: "Invisalign",
		introSecondMiddle:
			" es una excelente opción para quienes buscan una alternativa a los ",
		introSecondBraces: "brackets tradicionales",
		introSecondSuffix: ". Estamos aquí para ayudarte a determinar si ",
		introSecondFinalLink: "Invisalign",
		introSecondEnd: " es adecuado para ti.",
		whatHeading: "¿Qué es Invisalign?",
		whatParagraphs: [
			"El sistema Invisalign está hecho de alineadores transparentes y livianos de plástico que se ajustan sobre tus dientes y están diseñados para enderezar tu sonrisa. Son removibles, lo que facilita que nuestros pacientes en el área de Miami puedan comer, beber, cepillarse y usar hilo dental.",
			"Al ser removibles, no necesitas aprender de nuevo a cepillarte ni usar hilo dental, ni cambiar tu dieta. Y como son cómodos, a menudo olvidarás que llevas puestos los alineadores. A nuestros pacientes de Invisalign en ",
			"Invisalign se siente mucho más cómodo que los brackets metálicos tradicionales y puede darte los resultados que siempre has querido.",
			"Probablemente usarás los alineadores Invisalign durante 9 a 15 meses y normalmente usarás entre 18 y 30 juegos durante tu tratamiento. Incluso comenzarás a notar cambios en solo unos meses.",
			"En tu consulta inicial en ",
			"Usas las bandejas aproximadamente 22 horas al día, pero puedes quitártelas para cepillarte los dientes y comer. Según tus necesidades individuales y el plan de tratamiento establecido por ",
		],
		contactLinkText: "Miami Orthodontics Specialists",
		contactSuffix: " les encanta el sistema, y a ti también te encantará.",
		homeLinkText: "Miami Orthodontic Specialists",
		initialConsultSuffix:
			", tomaremos escaneos digitales y radiografías para crear una imagen 3D de tu boca. Con estas imágenes hacemos tu primer juego de alineadores personalizados, y luego comienzas el proceso de enderezar tus dientes.",
		facebookLinkText: "Miami Orthodontic Specialists",
		traySuffix:
			", usarás varios juegos de alineadores durante tu tratamiento ortodóncico.",
		discoverHeading: "Descubre Invisalign en Miami",
		discoverFirst:
			"Visita nuestra oficina en Miami para explorar opciones de tratamiento ortodóncico y ver si Invisalign es la opción adecuada para ti. La Dra. Garcia-Paul está aquí para ayudarte durante tu camino ortodóncico.",
		discoverSecondPrefix: "Programa una ",
		discoverSecondLink: "consulta gratuita",
		discoverSecondSuffix: " hoy.",
	},
} satisfies Record<Locale, InvisalignMiamiLocalization>;

export function InvisalignMiamiPage() {
	const getLocalization = useLocalization(invisalignMiamiLocalization);
	const whatParagraphs = getLocalization("whatParagraphs");

	return (
		<Flex direction="column">
			<HeroStyle2
				heroImageUrl="/invisalign.jpg"
				title={getLocalization("title")}
				backgroundPosition="center"
			/>
			<Box className={styles.invisalignIntroBand}>
				<Box className={styles.invisalignNarrow}>
					<Heading as="h3" className={styles.invisalignSectionHeading}>
						{getLocalization("introHeading")}
					</Heading>
					<Text as="p" size="4">
						<Link to="/" className={styles.inlineLink}>
							Miami Orthodontic Specialists
						</Link>
						{getLocalization("introFirst")}
					</Text>
					<Text as="p" size="4">
						{getLocalization("introSecondPrefix")}
						<Link to="/invisalign" className={styles.inlineLink}>
							{getLocalization("introSecondInvisalign")}
						</Link>
						{getLocalization("introSecondMiddle")}
						<Link to="/life-with-braces" className={styles.inlineLink}>
							{getLocalization("introSecondBraces")}
						</Link>
						{getLocalization("introSecondSuffix")}
						<Link to="/invisalign-miami-fl" className={styles.inlineLink}>
							{getLocalization("introSecondFinalLink")}
						</Link>
						{getLocalization("introSecondEnd")}
					</Text>
				</Box>
			</Box>
			<Box className={styles.invisalignBlueBand}>
				<Box className={styles.invisalignNarrow}>
					<OptimizedImage
						src="/miami-invisalign-001.jpg"
						alt="miami fl orthodontics"
						width={2000}
						height={1335}
						className={styles.invisalignFeatureImage}
					/>
					<Box className={styles.invisalignColumnCopy}>
						<Heading as="h3" className={styles.invisalignSectionHeading}>
							{getLocalization("whatHeading")}
						</Heading>
						<Text as="p" size="4">
							{whatParagraphs[0]}
						</Text>
						<Text as="p" size="4">
							{whatParagraphs[1]}
							<Link to="/contact-us" className={styles.inlineLink}>
								{getLocalization("contactLinkText")}
							</Link>
							{getLocalization("contactSuffix")}
						</Text>
						<Text as="p" size="4">
							{whatParagraphs[2]}
						</Text>
						<Text as="p" size="4">
							{whatParagraphs[3]}
						</Text>
						<Text as="p" size="4">
							{whatParagraphs[4]}
							<Link to="/" className={styles.inlineLink}>
								{getLocalization("homeLinkText")}
							</Link>
							{getLocalization("initialConsultSuffix")}
						</Text>
						<Text as="p" size="4">
							{whatParagraphs[5]}
							<a
								href="https://www.facebook.com/miamibraces"
								className={styles.inlineLink}
							>
								{getLocalization("facebookLinkText")}
							</a>
							{getLocalization("traySuffix")}
						</Text>
					</Box>
				</Box>
			</Box>
			<Box className={styles.invisalignIntroBand}>
				<Grid
					columns={{ initial: "1", sm: "2" }}
					gap="7"
					className={styles.invisalignTwoColumn}
				>
					<Box className={styles.entryContent}>
						<Heading as="h3" className={styles.invisalignSectionHeading}>
							{getLocalization("discoverHeading")}
						</Heading>
						<Text as="p" size="4">
							{getLocalization("discoverFirst")}
						</Text>
						<Text as="p" size="4">
							{getLocalization("discoverSecondPrefix")}
							<Link to="/free-consult" className={styles.inlineLink}>
								{getLocalization("discoverSecondLink")}
							</Link>
							{getLocalization("discoverSecondSuffix")}
						</Text>
					</Box>
					<OptimizedImage
						src="/miami-invisalign-002.jpg"
						alt="miami invisalign"
						width={2000}
						height={1333}
						className={styles.invisalignFeatureImage}
					/>
				</Grid>
			</Box>
			<Box className={styles.formWrap}>
				<HomeConsultForm />
			</Box>
		</Flex>
	);
}
